"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.FormField = void 0;
exports.default = FormGroupField;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react2 = require("@linaria/react");
var _textInput = _interopRequireDefault(require("@ux/text-input"));
var _selectInput = _interopRequireDefault(require("@ux/select-input"));
var _merchButton = require("@site/pattern-library-core/merch-button");
var _TelephoneField = _interopRequireDefault(require("./fields/TelephoneField"));
var _RecaptchaField = _interopRequireDefault(require("./fields/RecaptchaField"));
var _intents = _interopRequireDefault(require("@site/pattern-library-utils/intents"));
var _density = require("@site/pattern-library-utils/density");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const FormField = exports.FormField = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "FormField",
  class: "pl-fpfomt4",
  vars: {
    "fpfomt4-1": [({
      textAlign = 'start'
    }) => textAlign]
  }
});
const TextField = /*#__PURE__*/(0, _react2.styled)(_textInput.default)({
  name: "TextField",
  class: "pl-tv1e5r2"
});
/**
 * FormGroupField component.
 * Renders different types of input fields based on the type prop.
 *
 * @param {Object} props React Props
 * @param {string} props.id The field's id
 * @param {boolean} props.required Whether input field is required
 * @param {string} props.type The input field's type
 * @param {string} props.label The input field's aria label
 * @param {string} props.placeholder The input field's placeholder
 * @param {string} props.requiredText The input field's aria-required text
 * @param {string} props.name The input field's name
 * @param {string} props.dataCy Data attribute for testing
 * @param {boolean} props.disabled If true, the input field is disabled and not interactive
 * @param {boolean} props.showCountryCode Only relevant for 'tel' type; if true, displays country code selection
 * @param {string} props.hiddenValue Optional value for hidden input types
 * @param {...Object} rest An object capturing all other props
 * @returns {React.ReactElement} FormGroupField component
 * @public
 */
function FormGroupField({
  id,
  required,
  type,
  label,
  placeholder,
  requiredText,
  errorMessage = '',
  name,
  dataCy = 'form-group-field',
  disabled = false,
  showCountryCode = true,
  hiddenValue = '',
  textAlign,
  ...otherFieldProps
}) {
  const [value, setValue] = (0, _react.useState)('');
  const [valid, setValid] = (0, _react.useState)(true);
  const typeName = type.toLocaleLowerCase();
  const pattern = typeName === 'email' ? '^\\S+@\\S+\\.\\S+$' : null;
  const dataCyName = `${dataCy}-${typeName}`;
  const {
    onChange,
    onError,
    onExpired,
    sitekey,
    className,
    items
  } = otherFieldProps;
  const validate = () => {
    const isInvalid = Boolean(value.length > 0 && pattern && !new RegExp(pattern).test(value)) || required && value.length === 0;
    setValid(!isInvalid);
  };
  const resetValidation = () => {
    setValid(true);
  };
  const renderField = () => {
    switch (typeName) {
      case 'hidden':
        return /*#__PURE__*/_react.default.createElement("input", {
          id: `input-${id}`,
          name: name,
          "data-cy": dataCyName,
          type: "hidden",
          "aria-hidden": true,
          value: hiddenValue
        });
      case 'recaptcha':
        // eslint-disable-next-line no-case-declarations
        return /*#__PURE__*/_react.default.createElement(_RecaptchaField.default, (0, _extends2.default)({}, otherFieldProps, {
          dataCy: dataCyName,
          onChange: onChange,
          onError: onError,
          onExpired: onExpired,
          sitekey: sitekey
        }));
      case 'submit':
        return /*#__PURE__*/_react.default.createElement(_merchButton.NSMerchButton, (0, _extends2.default)({}, otherFieldProps, {
          "data-cy": dataCyName,
          text: name,
          type: "submit",
          ariaLabel: label,
          width: otherFieldProps?.width || 'full',
          disabled: disabled,
          textAlign: textAlign
        }));
      case 'tel':
        return /*#__PURE__*/_react.default.createElement(_TelephoneField.default, (0, _extends2.default)({}, otherFieldProps, {
          id: id,
          required: required,
          label: label,
          placeholder: placeholder,
          name: name,
          dataCy: dataCyName,
          requiredText: requiredText || errorMessage,
          onChange: e => {
            onChange && onChange({
              value: e.value,
              name,
              valid: e.valid
            });
          },
          showCountryCode: showCountryCode
        }));
      case 'dropdown':
        return /*#__PURE__*/_react.default.createElement(_selectInput.default, (0, _extends2.default)({}, otherFieldProps, {
          id: `id-${id}`,
          name: name,
          label: label,
          required: required,
          placeholder: placeholder,
          "data-cy": "form-group-dropdown",
          onChange: v => {
            setValue(v);
            onChange && onChange({
              value: v,
              name
            });
          },
          className: className
        }), items && items.map((a, i) => /*#__PURE__*/_react.default.createElement("option", {
          key: i,
          value: a.value,
          id: `id-${a.id}`
        }, a.text)));
      default:
        return /*#__PURE__*/_react.default.createElement(TextField, (0, _extends2.default)({}, otherFieldProps, {
          id: `input-${id}`,
          "data-cy": dataCyName,
          name: name,
          label: label,
          required: required,
          "aria-required": required,
          "aria-label": label,
          placeholder: placeholder,
          value: value,
          onChange: v => {
            setValue(v);
            onChange && onChange({
              value: v,
              name
            });
          },
          onFocus: resetValidation,
          onBlur: validate,
          invalid: !valid,
          errorMessage: !valid ? requiredText || errorMessage : '',
          pattern: pattern
        }));
    }
  };
  return /*#__PURE__*/_react.default.createElement(FormField, {
    textAlign: textAlign
  }, renderField());
}
FormGroupField.propTypes = {
  id: _propTypes.default.string,
  required: _propTypes.default.bool,
  type: _propTypes.default.string,
  label: _propTypes.default.string,
  placeholder: _propTypes.default.string,
  requiredText: _propTypes.default.string,
  errorMessage: _propTypes.default.string,
  name: _propTypes.default.string,
  dataCy: _propTypes.default.string,
  onChange: _propTypes.default.func,
  disabled: _propTypes.default.bool,
  showCountryCode: _propTypes.default.bool,
  hiddenValue: _propTypes.default.string,
  ..._RecaptchaField.default.propTypes,
  otherFieldProps: _propTypes.default.oneOfType([_propTypes.default.shape(_RecaptchaField.default.propTypes), _propTypes.default.shape(_merchButton.NSMerchButton.propTypes), _propTypes.default.shape(_TelephoneField.default.propTypes), _propTypes.default.shape(_selectInput.default.propTypes), _propTypes.default.shape(TextField.propTypes)])
};